import { useFocusingList } from 'aem-libs-react-ts/src/hooks/accessibility/useFocusingList'
import { useInputGaTracking } from 'aem-libs-react-ts/src/hooks/useInputGaTracking'
import { inputId } from 'aem-libs-tests/src/constants/inputId'
import { createTestId } from 'aem-libs-tests/src/utility/createTestId'
import type { ArrayType } from 'aem-libs-ts/src/types/utility'
import { isDefined } from 'aem-libs-ts/src/utils/common/isDefined'

import { Label } from '../label'
import type { CommonInputProps } from '../types'
import { useFieldError } from '../useFieldError'
import { useFieldMeta } from '../useFieldMeta'
import { ValidationMessage } from '../validation-message'
import { ToggleButtonItem } from './ToggleButtonItem'
import * as ToggleButtonsStyled from './ToggleButtons.styled'
import type { ToggleButtonItemType, ToggleButtonValueType } from './types'

export interface ToggleButtonsProps<TListOrItemValue extends ToggleButtonValueType | ToggleButtonValueType[]>
  extends Omit<CommonInputProps, 'width'> {
  id?: string
  name?: string
  activeValue: TListOrItemValue
  disabled?: boolean
  useGrid?: boolean
  gridSize?: number
  items: ToggleButtonItemType<ArrayType<TListOrItemValue>>[]
  onChange: (activeItems: TListOrItemValue) => void
}

export function ToggleButtons<TListOrItemValue extends ToggleButtonValueType | ToggleButtonValueType[]>({
  id,
  activeValue,
  items,
  label,
  tip,
  disabled,
  trackingDescription,
  useGrid,
  gridSize,
  onChange,
  hideValidation,
  validationMessage,
  testId
}: ToggleButtonsProps<TListOrItemValue>) {
  const hasMultipleValues = Array.isArray(activeValue)
  const { trackInputChange } = useInputGaTracking({
    enableGaTracking: true,
    id: id,
    description: trackingDescription
  })

  const { elementRef, handleKeyDown } = useFocusingList<HTMLUListElement>({
    onSelect: (index) => {
      const item = items[index]
      if (item) {
        handleClickItem(item.value as ArrayType<TListOrItemValue>)
      }
    }
  })

  const isItemActive = (value: ArrayType<TListOrItemValue>) => {
    return hasMultipleValues ? !!activeValue.find((v) => v === value) : value === activeValue
  }

  const getNewItems = (value: ToggleButtonValueType) => {
    const activeItems = activeValue as ToggleButtonValueType[]
    const activeItem = activeItems.find((item) => item === value)
    return isDefined(activeItem) ? activeItems.filter((item) => item !== value) : [...activeItems, value]
  }

  const handleClickItem = (value: ArrayType<TListOrItemValue>) => {
    const newValue = (Array.isArray(activeValue) ? getNewItems(value) : value) as TListOrItemValue
    onChange(newValue)
    trackInputChange(value, 'button')
  }

  const { showError, ariaErrorProps, errorMessage } = useFieldError(id, validationMessage, { hideValidation })
  const fieldProps = useFieldMeta(id, { tip: tip }, ariaErrorProps)
  const itemAriaIndex = items?.findIndex((item) => isItemActive(item.value)) ?? -1

  return (
    <ToggleButtonsStyled.Container
      role={hasMultipleValues ? 'group' : 'radiogroup'}
      aria-labelledby={id}
      data-testid={createTestId(testId ?? 'ToggleButtons-wrapper', inputId.wrapper)}
      {...fieldProps}
    >
      <Label htmlFor={id} id={id} label={label} tip={tip} testId={testId ?? 'label'} />
      <ToggleButtonsStyled.Items
        $useGrid={useGrid}
        $gridSize={gridSize}
        data-testid={createTestId(testId ?? 'ToggleButtons-items', inputId.items)}
        aria-labelledby={id}
        aria-activedescendant={`${String(itemAriaIndex)}-items-${id}`}
        tabIndex={-1}
        onKeyDown={(e: React.KeyboardEvent<HTMLUListElement>) => {
          if (hasMultipleValues) {
            return
          }
          handleKeyDown(e)
        }}
        ref={elementRef}
      >
        {items.map((item, index) => (
          <ToggleButtonItem
            tabIndex={hasMultipleValues || isItemActive(item.value) || (itemAriaIndex === -1 && index === 0) ? 0 : -1}
            role={hasMultipleValues ? 'checkbox' : 'radio'}
            disabled={disabled}
            key={index}
            isActive={isItemActive(item.value)}
            onClick={handleClickItem}
            testId={testId ?? 'ToggleButtons-item'}
            id={`${String(index)}-items-${id}`}
            {...item}
          />
        ))}
      </ToggleButtonsStyled.Items>
      {showError && <ValidationMessage id={id} message={errorMessage} testId={testId} />}
    </ToggleButtonsStyled.Container>
  )
}
